/**
 * Chrome sound palette — global chrome UI sounds.
 *
 * The variant SFX palettes (Reading Room, Sudoku Pencilmarks, etc.) own
 * the game-feel sounds. This tiny palette owns the *chrome* sounds — the
 * popout opens, the popout closes, a row inside the popout is selected.
 *
 * Kept here (not inside a variant) because the chrome is global and
 * outlives any single variant. Composed from `juice/audio` primitives,
 * routed through `playIfNotMuted` so the mute pref applies automatically.
 *
 * The taste is "warm pluck softened by a low-pass" — slightly lower
 * pitched than the variant taps so the chrome doesn't compete with the
 * game's own sound vocabulary.
 */

import { audio } from '@/juice';

function synthesizePopoutOpen(): void {
  const ctx = audio.getAudioContext();
  const master = audio.getMasterGain();
  if (!ctx || !master) return;

  const t = ctx.currentTime;

  // Two short pluck tones — fundamental + a perfect fifth — sliding up.
  const fundamental = 392.0; // G4
  [fundamental, fundamental * 1.5].forEach((freq, i) => {
    const osc = audio.makeOscillator(ctx, { freq, type: 'sine' });
    osc.frequency.exponentialRampToValueAtTime(freq * 1.06, t + 0.18);

    const lp = ctx.createBiquadFilter();
    lp.type = 'lowpass';
    lp.frequency.setValueAtTime(1800, t);
    lp.Q.value = 0.5;

    const { node: g } = audio.envelope(ctx, {
      attack: 0.004,
      decay: 0.06,
      sustain: 0.35,
      release: 0.22,
      peak: i === 0 ? 0.16 : 0.09,
    });

    audio.connectThrough(osc, lp, g, master);
    const tStart = t + i * 0.018;
    osc.start(tStart);
    osc.stop(tStart + 0.32);
  });
}

function synthesizePopoutClose(): void {
  const ctx = audio.getAudioContext();
  const master = audio.getMasterGain();
  if (!ctx || !master) return;

  const t = ctx.currentTime;

  // Single soft pluck, lower than open, with a downward sweep.
  const osc = audio.makeOscillator(ctx, { freq: 330, type: 'sine' });
  osc.frequency.exponentialRampToValueAtTime(247, t + 0.16);

  const lp = ctx.createBiquadFilter();
  lp.type = 'lowpass';
  lp.frequency.value = 1400;
  lp.Q.value = 0.4;

  const { node: g } = audio.envelope(ctx, {
    attack: 0.005,
    decay: 0.05,
    sustain: 0.3,
    release: 0.2,
    peak: 0.14,
  });

  audio.connectThrough(osc, lp, g, master);
  osc.start(t);
  osc.stop(t + 0.3);
}

function synthesizeRowTap(): void {
  const ctx = audio.getAudioContext();
  const master = audio.getMasterGain();
  if (!ctx || !master) return;

  const t = ctx.currentTime;
  const osc = audio.makeOscillator(ctx, { freq: 520, type: 'sine' });
  osc.frequency.exponentialRampToValueAtTime(680, t + 0.07);

  const { node: g } = audio.envelope(ctx, {
    attack: 0.003,
    decay: 0.04,
    sustain: 0.25,
    release: 0.12,
    peak: 0.18,
  });
  audio.connectThrough(osc, g, master);
  osc.start(t);
  osc.stop(t + 0.18);
}

export type ChromeSoundName = 'popout-open' | 'popout-close' | 'row-tap';

export function playChromeSound(name: ChromeSoundName): void {
  audio.playIfNotMuted(name, () => {
    audio.unlockAudio();
    switch (name) {
      case 'popout-open':
        synthesizePopoutOpen();
        return;
      case 'popout-close':
        synthesizePopoutClose();
        return;
      case 'row-tap':
        synthesizeRowTap();
        return;
    }
  });
}
